import React, { useState, useEffect, useCallback, useRef } from 'react';
import './SessionTimer.css';

// Get session duration from environment variable (in hours), default to 1 hour
// Convert to milliseconds
const SESSION_DURATION_HOURS = parseFloat(process.env.REACT_APP_SESSION_DURATION_HOURS || '1');
const SESSION_DURATION = SESSION_DURATION_HOURS * 60 * 60 * 1000; // Convert hours to milliseconds

const SessionTimer = ({ onSessionExpire }) => {
  const [timeRemaining, setTimeRemaining] = useState(SESSION_DURATION);
  const [isExpiring, setIsExpiring] = useState(false);
  const onSessionExpireRef = useRef(onSessionExpire);

  // Keep ref updated
  useEffect(() => {
    onSessionExpireRef.current = onSessionExpire;
  }, [onSessionExpire]);

  const handleSessionExpire = useCallback(() => {
    // Clear session data
    localStorage.removeItem('sessionStartTime');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    
    // Trigger logout callback
    if (onSessionExpireRef.current) {
      onSessionExpireRef.current();
    }
  }, []);

  useEffect(() => {
    // Get session start time from localStorage
    const sessionStartTime = localStorage.getItem('sessionStartTime');
    
    if (!sessionStartTime) {
      // If no session start time, set it now
      localStorage.setItem('sessionStartTime', Date.now().toString());
      setTimeRemaining(SESSION_DURATION);
    } else {
      // Calculate remaining time
      const elapsed = Date.now() - parseInt(sessionStartTime, 10);
      const remaining = Math.max(0, SESSION_DURATION - elapsed);
      setTimeRemaining(remaining);
      
      // If session already expired, trigger logout
      if (remaining === 0) {
        handleSessionExpire();
        return;
      }
    }

    // Update timer every second
    const interval = setInterval(() => {
      const startTime = parseInt(localStorage.getItem('sessionStartTime'), 10);
      if (!startTime) {
        clearInterval(interval);
        return;
      }

      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, SESSION_DURATION - elapsed);
      setTimeRemaining(remaining);

      // Show warning when less than 5 minutes remaining
      if (remaining < 5 * 60 * 1000 && remaining > 0) {
        setIsExpiring(true);
      }

      // If session expired, trigger logout
      if (remaining === 0) {
        clearInterval(interval);
        handleSessionExpire();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [handleSessionExpire]);

  // Format time as MM:SS
  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  // Calculate percentage for circular progress
  const percentage = (timeRemaining / SESSION_DURATION) * 100;
  
  // Calculate circumference and offset for circular progress (anti-clockwise)
  const circumference = 2 * Math.PI * 20; // radius = 20 for 50px circle
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div className={`session-timer ${isExpiring ? 'expiring' : ''}`} title={`Session expires in ${formatTime(timeRemaining)}`}>
      <svg className="timer-svg" width="50" height="50" viewBox="0 0 50 50">
        {/* Background circle */}
        <circle
          cx="25"
          cy="25"
          r="20"
          fill="none"
          stroke="rgba(255, 255, 255, 0.2)"
          strokeWidth="2.5"
        />
        {/* Progress circle (anti-clockwise) */}
        <circle
          cx="25"
          cy="25"
          r="20"
          fill="none"
          stroke={isExpiring ? '#ff6b6b' : '#4caf50'}
          strokeWidth="2.5"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 25 25)"
          className="timer-progress"
        />
      </svg>
      <div className="timer-text">{formatTime(timeRemaining)}</div>
      {isExpiring && <div className="timer-warning">⚠</div>}
    </div>
  );
};

export default SessionTimer;

